import { Anchor, type LinkShape } from '../../Anchor';
import { IconResolver } from '../../IconResolver';
import { NavOverlay, type NavProps } from '..';
import { AnimatePresence, cubicBezier, motion } from 'motion/react';
import { VocovoLogo } from '../DesktopNav';
import { useEffect } from 'react';
import { useState } from 'react';
import { useRef } from 'react';
import { usePathname } from 'next/navigation';
import { NavLinkMobile } from './NavLinkMobile';
import { ButtonLink } from '../../Inputs/Button';
import { ButtonCtaAction } from '../DesktopNav';
import { NavDropdownMobile } from './NavDropdownMobile';
import { showHeaderAtom } from '../../Header';
import { useAtomValue } from 'jotai';
import { MobileTabData } from './MobileTab';
import { useMeasure } from 'react-use';
import { useRectTracker } from '../../_lib/useRectTracker';

export type DesktopNavProps = NavProps;

export const smoothMenuTransition = {
	type: 'keyframes',
	ease: cubicBezier(0.33, 1, 0.68, 1),
};

export function MobileNav(props: DesktopNavProps) {
	const pathname = usePathname();
	const menuMobileRef = useRef<HTMLAnchorElement>(null);

	const [isNavOpen, setIsNavOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<number>();

	if (isNavOpen === false && activeTab !== undefined) {
		setActiveTab(undefined);
	}

	const showHeader = useAtomValue(showHeaderAtom);

	useEffect(() => {
		if (showHeader === false) {
			setIsNavOpen(false);
			setActiveTab(undefined);
		}
	}, [showHeader]);

	const screenWidth = useRectTracker().windowWidth;

	return (
		<>
			<div className='lg:hidden block relative z-30 max-h-screen'>
				<motion.div
					className='relative flex flex-col justify-between h-fit z-10 bg-background-white rounded-[1.5rem] border border-solid border-purple-100/10 pointer-events-none w-auto max-w-[30rem]'
					initial={{ paddingLeft: '0.5rem', paddingRight: '0.5rem', paddingTop: '0.5rem', paddingBottom: '0.5rem', width: 'fit-content' }}
					animate={{
						paddingLeft: isNavOpen ? '0.75rem' : '0.5rem',
						paddingRight: isNavOpen ? '0.75rem' : '0.5rem',
						paddingTop: isNavOpen ? '0.75rem' : '0.5rem',
						paddingBottom: isNavOpen ? '0.75rem' : '0.5rem',
						width: screenWidth <= 400 && isNavOpen ? '100%' : 'fit-content',
					}}
					transition={{ duration: 0.25 }}
				>
					<div className='relative flex items-center w-fit h-fit rounded-[1.5rem] z-[2] pointer-events-auto'>
						<a
							ref={menuMobileRef}
							className='p-1 before:bottom-0 before:left-0 before:absolute before:right-0 before:top-0 before:content[""] cursor-pointer text-purple-160!'
							onClick={e => {
								e.preventDefault();
								setIsNavOpen(prev => !prev);
							}}
						>
							{isNavOpen ? <IconResolver icon='x' className='w-6 h-6' /> : <IconResolver icon='list' className='w-6 h-6' />}
						</a>

						<div className='px-3'>
							<Anchor
								href={pathname === '/' ? '#top' : '/'}
								aria-label='Go to homepage'
								className='relative z-20 [&_svg]:text-purple-160! [&_svg]:w-23 [&_svg]:h-7'
							>
								<VocovoLogo defaultBrand={props.defaultBrand} />
							</Anchor>
						</div>
					</div>
					<AnimatePresence>
						{isNavOpen && (
							<motion.div
								className='flex flex-col max-h-[calc(100dvh-5.3rem)] overflow-y-auto pointer-events-auto'
								initial={{ opacity: 0, width: 0, height: 0, minWidth: 0 }}
								animate={{
									opacity: 1,
									width: screenWidth > 400 ? 'auto' : '100%',
									height: 'auto',
									minWidth: screenWidth > 520 ? '28.375rem' : screenWidth > 400 ? '22rem' : '100%',
									transition: {
										opacity: { duration: 0.3, delay: 0.5 },
										width: { duration: 0.5, delay: 0, ...smoothMenuTransition },
										height: { duration: 0.5, delay: 0, ...smoothMenuTransition },
										minWidth: { duration: 0.5, delay: 0, ...smoothMenuTransition },
									},
								}}
								exit={{
									opacity: 0,
									width: 0,
									height: 0,
									minWidth: 0,
									transition: {
										opacity: { duration: 0.1, delay: 0 },
										width: { duration: 0.5, delay: 0, ...smoothMenuTransition },
										height: { duration: 0.5, delay: 0, ...smoothMenuTransition },
										minWidth: { duration: 0.5, delay: 0, ...smoothMenuTransition },
									},
								}}
							>
								<NavMenuItems nav={props.nav} activeTab={activeTab} setActiveTab={setActiveTab} featuredText={props.featuredText} />

								{props.ctaNavButton && (
									<CTANavButtons item={props.ctaNavButton} type={'primary'} hasMobileMode={true} setClosed={setIsNavOpen} />
								)}
							</motion.div>
						)}
					</AnimatePresence>
				</motion.div>

				{props.ctaNavButton && (
					<div className='absolute top-0 right-0'>
						<ButtonCtaAction {...props.ctaNavButton} />
					</div>
				)}
			</div>
			<NavOverlay isNavOpen={isNavOpen} activeTab={activeTab} setActiveTab={setActiveTab} setIsNavOpen={setIsNavOpen} />
		</>
	);
}

function NavMenuItems(props: {
	nav: NavProps['nav'];

	activeTab: number | undefined;
	setActiveTab: (value: number | undefined) => void;
	featuredText?: string;
}) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [ref, { height }] = useMeasure<HTMLDivElement>();

	useEffect(() => {
		if (containerRef.current && props.activeTab === undefined) {
			containerRef.current.style.overflowY = 'hidden';
		}
	}, [props.activeTab]);

	return (
		<motion.div
			data-lenis-prevent
			ref={containerRef}
			animate={{ height: height || 'auto' }}
			transition={{ duration: 0.3 }}
			className='max-w-[30rem]'
			onAnimationStart={e => {
				if (containerRef.current) {
					containerRef.current.style.overflowY = 'hidden';
				}
			}}
			onAnimationComplete={e => {
				if (containerRef.current && props.activeTab !== undefined) {
					containerRef.current.style.overflowY = 'auto';
				}
			}}
		>
			<div ref={ref}>
				<AnimatePresence mode='wait'>
					{props.activeTab !== undefined ? (
						<motion.div
							key='tab-content'
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						>
							<MobileTabData {...props.nav[props.activeTab]} setActiveTab={props.setActiveTab} featuredText={props.featuredText} />
						</motion.div>
					) : (
						<motion.div
							key='nav-links'
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						>
							<div className='flex flex-col py-[0.75rem] rounded-[2.75rem] z-[2] text-purple-100'>
								{props.nav.map((Link, i) =>
									Link.groups ? (
										<NavDropdownMobile
											key={`nav-link-${Link.item.label}`}
											activeTab={props.activeTab}
											setActiveTab={props.setActiveTab}
											index={i}
											{...Link}
										/>
									) : (
										<NavLinkMobile key={`nav-link-${Link.item.label}`} {...Link.item} />
									),
								)}
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</motion.div>
	);
}

function CTANavButtons(props: {
	item?: LinkShape;
	type: 'primary' | 'secondary';
	hasMobileMode: boolean;
	setClosed: (value: boolean) => void;
}) {
	return (
		<div className='pt-[0.5rem] bg-background-white'>
			<ButtonLink
				parent='Nav'
				styleType={props.type}
				styleSize='large'
				className={`text-nowrap h-fit w-full`}
				href={props.item?.href}
				onClick={() => props.setClosed(false)}
			>
				{props.item?.label}
			</ButtonLink>
		</div>
	);
}
